import { useEffect, useMemo, useState } from "react";
import { Search, X, Menu } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { SECTIONS, buildAllQuestions } from "@/data/questions";
import {
  loadProgress,
  toggleMastered,
  countMastered,
  isMastered,
} from "@/lib/progress";
import { loadNotes, getNote, countNotes } from "@/lib/notes";
import { loadCustomQuestions } from "@/lib/customQuestions";
import Sidebar from "@/components/Sidebar";
import StatsBar from "@/components/StatsBar";
import QuestionCard from "@/components/QuestionCard";

export default function Home() {
  const [progress, setProgress] = useState({});
  const [notes, setNotes] = useState({});
  const [customQs, setCustomQs] = useState([]);
  const [activeSection, setActiveSection] = useState("all");
  const [activeGroup, setActiveGroup] = useState("all");
  const [search, setSearch] = useState("");
  const [hideMastered, setHideMastered] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setProgress(loadProgress());
    setNotes(loadNotes());
    setCustomQs(loadCustomQuestions());
  }, []);

  const allQuestions = useMemo(() => buildAllQuestions(customQs), [customQs]);
  const totalQuestions = allQuestions.length;

  const handleToggleMastered = (qid) => {
    const next = toggleMastered(progress, qid);
    setProgress(next);
  };

  const handleNoteChange = (qid, text) => {
    setNotes((prev) => {
      const next = { ...prev };
      if (text && text.trim()) {
        next[qid] = { text: text.trim(), ts: Date.now() };
      } else {
        delete next[qid];
      }
      return next;
    });
  };

  const counts = useMemo(() => {
    const bySection = {};
    SECTIONS.forEach((s) => {
      bySection[s.id] = allQuestions.filter((q) => q.sectionId === s.id).length;
    });
    return { all: totalQuestions, bySection };
  }, [allQuestions, totalQuestions]);

  const sectionObj = SECTIONS.find((s) => s.id === activeSection);
  const groupsForSection = sectionObj ? sectionObj.groups : [];

  const filtered = useMemo(() => {
    let list = allQuestions;
    if (activeSection !== "all") {
      list = list.filter((q) => q.sectionId === activeSection);
    }
    if (activeGroup !== "all") {
      list = list.filter((q) => q.groupId === activeGroup);
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter(
        (q) =>
          q.text.toLowerCase().includes(s) ||
          q.sectionTitle.toLowerCase().includes(s) ||
          q.groupTitle.toLowerCase().includes(s)
      );
    }
    if (hideMastered) {
      list = list.filter((q) => !isMastered(progress, q.id));
    }
    return list;
  }, [allQuestions, activeSection, activeGroup, search, hideMastered, progress]);

  const masteredCount = countMastered(progress);

  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-100 bg-grid">
      <Sidebar
        activeSection={activeSection}
        onSelectSection={(id) => {
          setActiveSection(id);
          setActiveGroup("all");
        }}
        counts={counts}
      />

      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-zinc-950/85 backdrop-blur border-b border-zinc-800">
          <div className="px-5 md:px-8 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
            <div className="flex items-center gap-3 lg:hidden">
              <button
                data-testid="mobile-nav-toggle"
                onClick={() => setMobileNavOpen((v) => !v)}
                className="text-zinc-300 hover:text-sky-400 transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
              <Link to="/" className="font-display text-lg font-bold">
                AIS · ORAL
              </Link>
            </div>

            <div className="relative flex-1 max-w-2xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une question, un mot-clé…"
                className="pl-9 pr-9 bg-zinc-900 border-zinc-800 rounded-none text-sm h-10 focus-visible:ring-sky-500/40 focus-visible:ring-offset-0 focus-visible:border-sky-500"
              />
              {search && (
                <button
                  data-testid="clear-search"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {activeSection !== "all" && groupsForSection.length > 1 && (
                <Select value={activeGroup} onValueChange={setActiveGroup}>
                  <SelectTrigger
                    data-testid="group-select"
                    className="bg-zinc-900 border-zinc-800 rounded-none h-10 w-[200px] text-sm"
                  >
                    <SelectValue placeholder="Groupe" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les groupes</SelectItem>
                    {groupsForSection.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <div className="flex items-center gap-2">
                <Switch
                  data-testid="hide-mastered-toggle"
                  id="hide-mastered"
                  checked={hideMastered}
                  onCheckedChange={setHideMastered}
                  className="data-[state=checked]:bg-sky-500"
                />
                <Label
                  htmlFor="hide-mastered"
                  className="text-xs uppercase tracking-widest text-zinc-400 cursor-pointer"
                >
                  Cacher maîtrisées
                </Label>
              </div>
            </div>
          </div>

          {/* Mobile section nav */}
          {mobileNavOpen && (
            <div className="lg:hidden border-t border-zinc-800 px-5 py-3 flex flex-wrap gap-2">
              <SectionPill
                active={activeSection === "all"}
                onClick={() => {
                  setActiveSection("all");
                  setActiveGroup("all");
                  setMobileNavOpen(false);
                }}
                label="Toutes"
                testid="mobile-section-all"
              />
              {SECTIONS.map((s) => (
                <SectionPill
                  key={s.id}
                  active={activeSection === s.id}
                  onClick={() => {
                    setActiveSection(s.id);
                    setActiveGroup("all");
                    setMobileNavOpen(false);
                  }}
                  label={s.short}
                  testid={`mobile-section-${s.id}`}
                />
              ))}
              <Link
                to="/flashcards"
                data-testid="mobile-nav-flashcards"
                className="text-xs uppercase tracking-widest px-3 py-1.5 border border-sky-500/40 text-sky-400"
              >
                Flashcards →
              </Link>
            </div>
          )}
        </div>

        <div className="px-5 md:px-8 py-6 md:py-8 space-y-8">
          {/* Hero / heading */}
          <header className="space-y-4">
            <div className="text-[11px] uppercase tracking-[0.3em] text-sky-400 font-bold">
              Titre Professionnel AIS · oral technique
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
              Prépare ton oral{" "}
              <span className="text-sky-400">comme un pro</span>.
            </h1>
            <p className="text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed">
              58 questions du jury extraites de ton document, organisées par
              section et par groupe. Génère une réponse modèle structurée par
              IA en un clic, puis marque les questions maîtrisées pour suivre
              ta progression.
            </p>
            <div className="flex flex-wrap gap-3 pt-1">
              <Link
                to="/exam"
                data-testid="cta-exam"
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-zinc-950 font-bold px-5 py-2.5 text-sm transition-colors uppercase tracking-wider"
              >
                Lancer un examen chronométré
              </Link>
              <Link
                to="/flashcards"
                data-testid="cta-flashcards"
                className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 px-5 py-2.5 text-sm transition-colors uppercase tracking-wider text-zinc-200"
              >
                Démarrer en flashcards
              </Link>
              <Link
                to="/antiseche"
                data-testid="cta-antiseche"
                className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 px-5 py-2.5 text-sm transition-colors uppercase tracking-wider text-zinc-300"
              >
                Antisèche IA
              </Link>
              <Link
                to="/import"
                data-testid="cta-import"
                className="inline-flex items-center gap-2 border border-sky-500/40 hover:bg-sky-500/10 text-sky-400 px-5 py-2.5 text-sm transition-colors uppercase tracking-wider"
              >
                + Importer un fichier
              </Link>
            </div>
          </header>

          {/* Stats */}
          <StatsBar
            total={totalQuestions}
            filtered={filtered.length}
            mastered={masteredCount}
            notesCount={countNotes(notes)}
          />

          {/* Active section description */}
          {sectionObj && (
            <div
              data-testid="section-header"
              className="border-l-2 border-sky-500 pl-4 py-1"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                Section active
              </div>
              <h2 className="font-display text-xl md:text-2xl font-bold">
                {sectionObj.title}
              </h2>
              <p className="text-sm text-zinc-400 mt-1">
                {sectionObj.description}
              </p>
            </div>
          )}

          {/* Questions */}
          <section id="questions" className="space-y-3">
            {filtered.length === 0 ? (
              <div
                data-testid="empty-state"
                className="border border-dashed border-zinc-800 bg-zinc-950/40 p-10 text-center"
              >
                <div className="font-display text-lg text-zinc-300">
                  Aucune question ne correspond.
                </div>
                <p className="text-sm text-zinc-500 mt-1">
                  Essaie de modifier ta recherche ou de changer de section.
                </p>
              </div>
            ) : (
              filtered.map((q, i) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  index={i}
                  mastered={isMastered(progress, q.id)}
                  onToggleMastered={handleToggleMastered}
                  note={getNote(notes, q.id)}
                  onNoteChange={handleNoteChange}
                />
              ))
            )}
          </section>

          <footer className="pt-8 pb-6 border-t border-zinc-800 text-[11px] uppercase tracking-[0.25em] text-zinc-600">
            Bonne chance pour ton oral · réponses IA · GPT-5.2
          </footer>
        </div>
      </main>
    </div>
  );
}

function SectionPill({ active, onClick, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
        active
          ? "border-sky-400 text-sky-400 bg-sky-500/10"
          : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
      }`}
    >
      {label}
    </button>
  );
}
